import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, FilmSlate, Export, Gear, FloppyDisk, Eye, Lightning, Spinner, Copy, ArrowsClockwise, ClockCounterClockwise, Microphone, Question } from '@phosphor-icons/react';
import { SceneList, SortableSceneItem, SceneItemDragOverlay } from '@/components/SceneList';
import { VisualPreview } from '@/components/VisualPreview';
import { Timeline } from '@/components/Timeline';
import { ScriptUploader } from '@/components/ScriptUploader';
import { ExportModal } from '@/components/ExportModal';
import { ProjectSettings } from '@/components/ProjectSettings';
import { StockSearchModal } from '@/components/StockSearchModal';
import { CoverageIndicator } from '@/components/CoverageIndicator';
import { DuplicateDetector } from '@/components/DuplicateDetector';
import { BatchRegenerateModal } from '@/components/BatchRegenerateModal';
import { VersionHistoryModal } from '@/components/VersionHistoryModal';
import { VoiceoverModal } from '@/components/VoiceoverModal';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageToggle } from '@/components/LanguageToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [project, setProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [selectedScene, setSelectedScene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showStockSearch, setShowStockSearch] = useState(false);
  const [showDuplicateDetector, setShowDuplicateDetector] = useState(false);
  const [showBatchRegenerate, setShowBatchRegenerate] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showVoiceoverModal, setShowVoiceoverModal] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchProject = useCallback(async (selectFirst = false) => {
    try {
      const [projectRes, scenesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/scenes`)
      ]);
      setProject(projectRes.data);
      setScenes(scenesRes.data);
      if (selectFirst && scenesRes.data.length > 0) {
        setSelectedScene(scenesRes.data[0]);
      }
    } catch (err) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject(true);
  }, []);

  const handleScriptProcessed = (newScenes, totalDuration) => {
    setScenes(newScenes);
    if (newScenes.length > 0) {
      setSelectedScene(newScenes[0]);
    }
    setProject(prev => ({ ...prev, scene_count: newScenes.length, total_duration: totalDuration }));
    toast.success(`Created ${newScenes.length} scenes`);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = scenes.findIndex(s => s.id === active.id);
      const newIndex = scenes.findIndex(s => s.id === over.id);
      const reorderedScenes = arrayMove(scenes, oldIndex, newIndex);
      setScenes(reorderedScenes);

      try {
        await axios.post(`${API}/projects/${projectId}/reorder-scenes`, {
          scene_ids: reorderedScenes.map(s => s.id)
        });
      } catch (err) {
        toast.error('Failed to reorder scenes');
        fetchProject();
      }
    }
  };

  const handleUpdateScene = async (sceneId, updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/projects/${projectId}/scenes/${sceneId}`, updates);
      setScenes(prev => prev.map(s => s.id === sceneId ? res.data : s));
      if (selectedScene?.id === sceneId) {
        setSelectedScene(res.data);
      }
    } catch (err) {
      toast.error('Failed to update scene');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteScene = async (sceneId) => {
    if (!window.confirm('Delete this scene?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/scenes/${sceneId}`);
      const updated = scenes.filter(s => s.id !== sceneId);
      setScenes(updated);
      if (selectedScene?.id === sceneId) {
        setSelectedScene(updated[0] || null);
      }
      setProject(prev => ({ ...prev, scene_count: updated.length }));
      toast.success('Scene deleted');
    } catch (err) {
      toast.error('Failed to delete scene');
    }
  };

  const handleDuplicateScene = async (sceneId) => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/scenes/${sceneId}/duplicate`);
      await fetchProject();
      setSelectedScene(res.data);
      toast.success('Scene duplicated');
    } catch (err) {
      toast.error('Failed to duplicate scene');
    }
  };

  const handleGenerateImage = async (sceneId, prompt, aspectRatio = '16:9') => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/scenes/${sceneId}/generate-image`, { prompt, aspect_ratio: aspectRatio });
      if (res.data.success) {
        await handleUpdateScene(sceneId, { 
          asset_url: res.data.image_url,
          asset_source: res.data.source,
          license_type: res.data.license
        });
        toast.success('Image generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate image');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectStockAsset = async (asset) => {
    if (!selectedScene) return;
    setShowStockSearch(false);
    await handleUpdateScene(selectedScene.id, {
      asset_url: asset.url,
      asset_source: asset.source,
      license_type: asset.license,
      attribution: asset.attribution
    });
    toast.success('Asset selected');
  };

  const handleUpdateAspectRatio = async (sceneId, aspectRatio) => {
    await handleUpdateScene(sceneId, { aspect_ratio: aspectRatio });
  };

  const handleBatchGenerate = async () => {
    const scenesWithoutImages = scenes.filter(s => !s.asset_url);
    if (scenesWithoutImages.length === 0) {
      toast.info('All scenes already have images');
      return;
    }

    const confirmed = window.confirm(
      `Generate images for ${scenesWithoutImages.length} scene(s)?\n\nThis will take approximately ${Math.ceil(scenesWithoutImages.length * 17 / 60)} minutes.\n\nYou can close this dialog and the generation will continue. Refresh the page to see progress.`
    );
    if (!confirmed) return;

    setBatchGenerating(true);
    
    // Generate images one by one with progress updates
    let generated = 0;
    let failed = 0;
    
    for (const scene of scenesWithoutImages) {
      try {
        toast.info(`Generating scene ${scene.scene_number} of ${scenes.length}... (${generated} done)`, { id: 'batch-progress' });
        
        const res = await axios.post(`${API}/scenes/${scene.id}/generate-image`, {
          prompt: scene.visual_prompt,
          aspect_ratio: scene.aspect_ratio || '16:9'
        });
        
        if (res.data.success) {
          generated++;
          // Update local state to show progress
          setScenes(prev => prev.map(s => 
            s.id === scene.id 
              ? { ...s, asset_url: res.data.image_url, asset_source: res.data.source, license_type: res.data.license }
              : s
          ));
        } else {
          failed++;
        }
      } catch (err) {
        console.error(`Failed to generate scene ${scene.scene_number}:`, err);
        failed++;
        // Continue with next scene instead of stopping
      }
    }
    
    setBatchGenerating(false);
    toast.dismiss('batch-progress');
    
    if (generated > 0) {
      toast.success(`Generated ${generated} images successfully!`);
    }
    if (failed > 0) {
      toast.warning(`${failed} scenes failed to generate. You can retry them individually.`);
    }
    
    // Refresh to get final state
    await fetchProject();
  };

  const handleUpdateProject = async (updates) => {
    try {
      const res = await axios.put(`${API}/projects/${projectId}`, updates);
      setProject(res.data);
      toast.success('Project updated');
    } catch (err) {
      toast.error('Failed to update project');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF5D01] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeScene = activeId ? scenes.find(s => s.id === activeId) : null;

  return (
    <div className="h-screen bg-[#0a0a0a] flex flex-col overflow-hidden">
      {/* Top Toolbar */}
      <header className="bg-[#171717] border-b border-[#333333] px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-[#A1A1AA] hover:text-white hover:bg-[#262626] gap-2"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft size={20} />
            {language === 'es' ? 'Panel' : 'Dashboard'}
          </Button>
          <div className="h-6 w-px bg-[#333333]" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF5D01] rounded flex items-center justify-center">
              <FilmSlate size={18} weight="fill" className="text-white" />
            </div>
            <span className="text-white font-semibold truncate max-w-[200px]">{project?.title}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-[#A1A1AA] text-sm mr-2">
            <span className="text-white">{scenes.filter(s => s.asset_url).length}</span>/{scenes.length} {language === 'es' ? 'imágenes' : 'images'} • {formatDuration(project?.total_duration || 0)} {language === 'es' ? 'total' : 'total'}
            {saving && <span className="ml-2 text-[#FF5D01]">{language === 'es' ? 'Guardando...' : 'Saving...'}</span>}
            {batchGenerating && <span className="ml-2 text-[#2563EB]">{language === 'es' ? 'Generando...' : 'Generating...'}</span>}
          </div>
          <CoverageIndicator projectId={projectId} scenesCount={scenes.length} />
          {scenes.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleBatchGenerate}
              disabled={batchGenerating || saving}
              className="border-[#333333] text-white hover:bg-[#262626] gap-2"
              data-testid="batch-generate-btn"
            >
              {batchGenerating ? (
                <div className="w-4 h-4 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Lightning size={18} weight="fill" />
              )}
              {batchGenerating ? (language === 'es' ? 'Generando...' : 'Generating...') : `${language === 'es' ? 'Generar Todo' : 'Generate All'} (${scenes.filter(s => !s.asset_url).length})`}
            </Button>
          )}
          {scenes.filter(s => s.asset_url).length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBatchRegenerate(true)}
              disabled={batchGenerating || saving}
              className="border-[#333333] text-[#A1A1AA] hover:text-white hover:bg-[#262626] gap-2"
              data-testid="batch-regenerate-btn"
              title={language === 'es' ? 'Regenerar imágenes seleccionadas con mejora de estilo' : 'Regenerate selected images with style enhancement'}
            >
              <ArrowsClockwise size={18} />
              {language === 'es' ? 'Regenerar' : 'Regenerate'}
            </Button>
          )}
          {scenes.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowVoiceoverModal(true)}
              disabled={batchGenerating || saving}
              className="border-[#333333] text-[#A1A1AA] hover:text-white hover:bg-[#262626] gap-2"
              data-testid="voiceover-btn"
              title={language === 'es' ? 'Generar voces en off con IA para escenas' : 'Generate AI voiceovers for scenes'}
            >
              <Microphone size={18} />
              {language === 'es' ? 'Voz en Off' : 'Voiceover'}
            </Button>
          )}
          {scenes.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDuplicateDetector(true)}
              className="text-[#A1A1AA] hover:text-white hover:bg-[#262626]"
              data-testid="duplicate-detector-btn"
              title={language === 'es' ? 'Detectar escenas duplicadas' : 'Detect duplicate scenes'}
            >
              <Copy size={20} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowVersionHistory(true)}
            className="text-[#A1A1AA] hover:text-white hover:bg-[#262626]"
            data-testid="version-history-btn"
            title={language === 'es' ? 'Historial de versiones' : 'Version history'}
          >
            <ClockCounterClockwise size={20} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettingsModal(true)}
            className="text-[#A1A1AA] hover:text-white hover:bg-[#262626]"
            data-testid="project-settings-btn"
            title={language === 'es' ? 'Configuración' : 'Settings'}
          >
            <Gear size={20} />
          </Button>
          <LanguageToggle />
          <a
            href="/guide.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#A1A1AA] hover:text-white transition-colors p-2"
            title={t('userGuide')}
            data-testid="editor-user-guide-link"
          >
            <Question size={20} weight="bold" />
          </a>
          <Button
            onClick={() => setShowExportModal(true)}
            className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
            data-testid="export-btn"
          >
            <Export size={18} weight="bold" />
            {language === 'es' ? 'Exportar' : 'Export'}
          </Button>
        </div>
      </header>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Left Panel - Scene List */}
          <div className="w-80 bg-[#171717] border-r border-[#333333] flex flex-col flex-shrink-0">
            <div className="p-4 border-b border-[#333333]">
              <ScriptUploader
                projectId={projectId}
                project={project}
                onScriptProcessed={handleScriptProcessed}
              />
            </div>
            <SortableContext items={scenes.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <SceneList
                scenes={scenes}
                selectedScene={selectedScene}
                onSelectScene={setSelectedScene}
                onDeleteScene={handleDeleteScene}
                onDuplicateScene={handleDuplicateScene}
                onUpdateAspectRatio={handleUpdateAspectRatio}
              />
            </SortableContext>
          </div>

          {/* DragOverlay for visual feedback */}
          <DragOverlay>
            {activeScene && <SceneItemDragOverlay scene={activeScene} />}
          </DragOverlay>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Visual Preview */}
            <div className="flex-1 p-6 overflow-auto">
              {selectedScene ? (
                <VisualPreview
                  scene={selectedScene}
                  project={project}
                  onUpdateScene={handleUpdateScene}
                  onGenerateImage={handleGenerateImage}
                  onSearchStock={() => setShowStockSearch(true)}
                  saving={saving}
                />
              ) : scenes.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-[#171717] rounded-full flex items-center justify-center mx-auto mb-4">
                      <FilmSlate size={40} className="text-[#333333]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">No Scenes Yet</h3>
                    <p className="text-[#A1A1AA] max-w-sm">
                      Upload or paste your script in the left panel to automatically generate scenes.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-[#A1A1AA]">Select a scene to view and edit</p>
                </div>
              )}
            </div>

            {/* Timeline */}
            {scenes.length > 0 && (
              <Timeline
                scenes={scenes}
                selectedScene={selectedScene}
                onSelectScene={setSelectedScene}
                totalDuration={project?.total_duration || 0}
              />
            )}
          </div>
        </DndContext>
      </div>

      {/* Modals */}
      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        projectId={projectId}
        projectTitle={project?.title}
        scenes={scenes}
      />
      
      <ProjectSettings
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        project={project}
        onUpdate={handleUpdateProject}
      />
      
      <StockSearchModal
        open={showStockSearch}
        onClose={() => setShowStockSearch(false)}
        onSelect={handleSelectStockAsset}
        initialQuery={selectedScene?.visual_prompt || ''}
        mediaType={selectedScene?.media_type || 'image'}
      />
      
      <DuplicateDetector
        open={showDuplicateDetector}
        onClose={() => setShowDuplicateDetector(false)}
        projectId={projectId}
        onDeleteScene={handleDeleteScene}
      />
      
      <BatchRegenerateModal
        open={showBatchRegenerate}
        onClose={() => setShowBatchRegenerate(false)}
        projectId={projectId}
        scenes={scenes}
        onRegenerated={() => fetchProject(false)}
      />
      
      <VersionHistoryModal
        open={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        projectId={projectId}
        onRestored={() => fetchProject(false)}
      />
      
      <VoiceoverModal
        open={showVoiceoverModal}
        onClose={() => setShowVoiceoverModal(false)}
        projectId={projectId}
        scenes={scenes}
        onGenerated={() => fetchProject(false)}
      />
    </div>
  );
}
